import { Clock, Flame } from "lucide-react";

const FILL = {
  easy: "bg-emerald-500",
  moderate: "bg-brand-yellow",
  hard: "bg-brand-orange",
  rest: "bg-muted-foreground/40",
};
const DOT = {
  easy: "bg-emerald-500",
  moderate: "bg-brand-yellow",
  hard: "bg-brand-orange",
  rest: "bg-muted-foreground/40",
};

export default function PlannedView({ segments = [] }) {
  const segs = (segments || []).filter((s) => s && (s.minutes || s.label));
  if (!segs.length) return null;
  const total = segs.reduce((a, s) => a + (Number(s.minutes) || 0), 0) || 1;

  const byI = { easy: 0, moderate: 0, hard: 0, rest: 0 };
  segs.forEach((s) => { byI[s.intensity] = (byI[s.intensity] || 0) + (Number(s.minutes) || 0); });
  const pctOf = (k) => Math.round((byI[k] / total) * 100);
  const hardPct = pctOf("hard"), modPct = pctOf("moderate");
  const focusLine = hardPct >= 20
    ? `High-intensity stimulus — ~${byI.hard} min of hard work to lift your top end.`
    : modPct >= 25
    ? `Tempo / threshold work — sustained effort to raise your sustainable pace.`
    : `Aerobic base — mostly easy Zone 2 to build endurance without fatigue.`;
  const dist = [
    ["Easy", pctOf("easy"), "bg-emerald-500"],
    ["Moderate", modPct, "bg-brand-yellow"],
    ["Hard", hardPct, "bg-brand-orange"],
  ].filter((d) => d[1] > 0);

  return (
    <div data-testid="planned-view">
      {/* what your body trains today */}
      <div className="rounded-xl border border-border bg-muted/40 p-4 mb-4" data-testid="training-focus">
        <div className="flex items-center gap-1.5 mb-2">
          <Flame size={14} className="text-brand-orange" />
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">What today trains</p>
        </div>
        <p className="text-sm text-foreground leading-relaxed" data-testid="focus-line">{focusLine}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5" data-testid="focus-distribution">
          {dist.map(([label, pct, dot]) => (
            <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`w-2 h-2 rounded-full ${dot}`} /> {label} <span className="font-semibold text-foreground">{pct}%</span>
            </span>
          ))}
        </div>
      </div>

      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Session structure</p>

      {/* intensity timeline */}
      <div className="flex w-full h-9 rounded-xl overflow-hidden border border-border" data-testid="planned-timeline">
        {segs.map((s, i) => {
          const pct = ((Number(s.minutes) || 0) / total) * 100;
          const fill = FILL[s.intensity] || FILL.easy;
          return (
            <div key={i} className={`${fill} h-full flex items-center justify-center transition-all border-r border-background/40 last:border-r-0`} style={{ width: `${pct}%` }} title={`${s.label || ""} · ${s.minutes || 0} min`}>
              {pct > 12 && <span className="text-[10px] font-bold text-white/90 px-1 truncate">{s.minutes}′</span>}
            </div>
          );
        })}
      </div>

      {/* legend / detail list */}
      <div className="mt-3 space-y-2" data-testid="planned-segments">
        {segs.map((s, i) => (
          <div key={i} className="flex items-start gap-2.5 text-sm">
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${DOT[s.intensity] || DOT.easy}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-foreground truncate">{s.label || "Block"}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0"><Clock size={12} /> {s.minutes || 0} min</span>
              </div>
              {(s.zone || s.target) && (
                <p className="text-xs text-muted-foreground">
                  {s.zone ? <span className="font-medium text-foreground/80">{s.zone}</span> : null}
                  {s.zone && s.target ? " · " : ""}
                  {s.target || ""}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
